import * as React from 'react';
import BPromise from 'bluebird';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { getCreatedAt, getRequestRole, getRequestSports } from 'Src/helpers/accessor/accessor';
import { Request } from 'Src/models/request';
import {
  getAllRequests,
  getRequests,
  getRequestsCount,
  mergeUserStaff,
  updateRequest
} from 'Src/helpers/service/admin/requests';
import { AcceptRequestModal } from 'Src/components/AcceptRequestModal/AcceptRequestModal';
import {
  isUserActiveSchoolSubscriptionPlanFree,
  isUserActiveSchoolSubscriptionPlanFull,
  isUserAdminSU
} from 'Src/helpers/user/user';
import { PERMISSION_REQUEST_STATUS } from 'Src/consts/permissionRequest';
import { ROLE, ROLE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';
import { AcceptAndMergeRequestForm } from 'Src/components/AcceptRequestModal/AcceptAndMergeRequestForm';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { AcceptAndMergeRequestInfoModal } from 'Src/components/AcceptRequestModal/AcceptAndMergeRequestInfoModal';
import { SimpleAlert } from 'Src/components/SimpleAlert/SimpleAlert';
import propz from 'propz';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import { mergeStudent } from '../../../../../helpers/service/admin/students';
import '../Students/Students.scss';
import { PeopleColumnsMenu } from '../PeopleColumnsMenu';
import { countActivePeopleFilters, PeopleFilterChips } from '../PeopleFilterChips';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import moment from 'moment';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name or surname',
    field: 'nameSearch',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['requester', 'firstName']
  },
  {
    text: 'Date',
    field: 'createdAt',
    isSort: false,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getCreatedAt
  },
  {
    text: 'Name',
    field: 'requester.firstName',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['requester', 'firstName']
  },
  {
    text: 'Surname',
    field: 'requester.lastName',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['requester', 'lastName']
  },
  {
    text: 'Phone',
    field: 'requester.phone',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['requester', 'phone']
  },
  {
    text: 'Email',
    field: 'requester.email',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['requester', 'email']
  },
  {
    text: 'Role',
    field: 'requestedPermissionPreset',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getRequestRole
  },
  {
    text: 'Comment',
    field: 'comment',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['requestedPermission', 'comment']
  },
  {
    text: 'Sports',
    field: 'sports',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getRequestSports
  }
];

const getRequestsWhere = (filters: any): any => {
  const { nameSearch, ...fieldFilters } = filters || {};
  const where = getServerFieldSectionWhere(fieldFilters);
  const term = String(nameSearch || '').trim();
  if (term) {
    where.nameSearch = { like: term, options: 'i' };
  }
  if (filters['requester.email']) {
    where['requester.email'] = { like: filters['requester.email'], options: 'i' };
  }
  if (filters['requester.phone']) {
    where['requester.phone'] = { like: filters['requester.phone'], options: 'i' };
  }
  const createdAt = filters.createdAt || {};
  if (createdAt.from || createdAt.to) {
    const range: any = {};
    if (createdAt.from) {
      range.$gte = moment(createdAt.from, ['DD-MM-YYYY', 'YYYY-MM-DD'], true)
        .startOf('day')
        .toISOString();
    }
    if (createdAt.to) {
      range.$lte = moment(createdAt.to, ['DD-MM-YYYY', 'YYYY-MM-DD'], true)
        .endOf('day')
        .toISOString();
    }
    where.createdAt = range;
  }
  return where;
};

const getRequestDateRange = (filters: any): any => {
  const createdAt = filters.createdAt || {};
  const parseDate = (value: string): Date | null => {
    const parsed = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
    return parsed.isValid() ? parsed.toDate() : null;
  };
  return { start: parseDate(createdAt.from || ''), end: parseDate(createdAt.to || '') };
};

interface State {
  items: Request[];
  mergeUser: any;
  currentPage: number;
  selectedItems: Request[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  previewItem?: Request;
  basePath: string;

  isAcceptModalOpen: boolean;
  isAcceptAndMergeModalOpen: boolean;
  isAcceptAndMergeRequestInfoModal: boolean;
  isAcceptRequestDisabledModalOpen: boolean;
  isRejectConfirmModalOpen: boolean;
  isError: boolean;
  hiddenColumns: string[];
  requestDateRange: any;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  setNeedUpdate: () => void;
  onTotalChange?: (total: number) => void;
}

export class Requests extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      mergeUser: undefined,
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      previewItem: undefined,
      basePath: '',

      isAcceptModalOpen: false,
      isAcceptAndMergeModalOpen: false,
      isAcceptAndMergeRequestInfoModal: false,
      isAcceptRequestDisabledModalOpen: false,
      isRejectConfirmModalOpen: false,
      isError: false,
      hiddenColumns: loadHiddenColumns(props.user, 'people.roleRequests.inbox'),
      requestDateRange: { start: null, end: null }
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
    document.addEventListener('mousedown', this.onFilterOutsideClick);
    document.addEventListener('keydown', this.onFilterKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onFilterOutsideClick);
    document.removeEventListener('keydown', this.onFilterKeyDown);
  }

  onFilterOutsideClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (!target.closest('.requests-filter') && !target.closest('.people-filterBtn')) {
      this.setState({ isShowFilter: false });
      if (this.state.isShowFilter) this.setItems();
    }
  };
  onFilterKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.setState({ isShowFilter: false });
      if (this.state.isShowFilter) this.setItems();
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps);
    }
  }

  setItems(props: Props = this.props) {
    const { user, location } = props;

    const search = parse(location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const where = getRequestsWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getRequests(user, serverQueryFilter);
    const getItemsCountPromise = getRequestsCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return BPromise.all(promises)
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection: sortDirection,
          sortColumnsName: sortColumnsName,
          isShowFilter: false,
          isDataFiltered: isDataFiltered,
          filters: filters,
          requestDateRange: getRequestDateRange(filters),
          items: items,
          total: countObj.count,
          basePath: location.pathname,
          isLoading: false
        });
        if (typeof props.onTotalChange === 'function') {
          props.onTotalChange(countObj.count);
        }
        return true;
      })
      .catch(error => {
        console.error('Error fetching data:', error);
        this.setState({ isLoading: false, items: [] });
        if (typeof this.props.onTotalChange === 'function') {
          this.props.onTotalChange(0);
        }
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getRequestsWhere(filters);

    getAllRequests(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      filters: {},
      requestDateRange: { start: null, end: null },
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  closeAcceptRequestModal = (): void => {
    this.setState({
      isAcceptModalOpen: false
    });
  };

  onAcceptRequestClick = () => {
    const user = this.props.user;
    const requests = this.state.items;
    const { selectedItems } = this.state;
    const selectedItem = selectedItems[0];
    const selectedItemId = selectedItem.id;

    const request = requests.find(requestState => requestState.id === selectedItemId);

    const isRequestedPermissionPresetCoach = request.requestedPermission.preset === ROLE.COACH;
    const isRequestedPermissionPresetTeacher = request.requestedPermission.preset === ROLE.TEACHER;
    const isRequestedPermissionPresetParent = request.requestedPermission.preset === ROLE.PARENT;
    const isRequestedPermissionPresetStudent = request.requestedPermission.preset === ROLE.STUDENT;

    const isSubscriptionPlanFull = isUserActiveSchoolSubscriptionPlanFull(user);

    switch (true) {
      case isRequestedPermissionPresetCoach:
      case isRequestedPermissionPresetTeacher:
      // for free communication school parent request accept without student
      case isRequestedPermissionPresetParent && isSubscriptionPlanFull:
      case isRequestedPermissionPresetStudent:
        this.props.history.push({
          pathname: `/requests/${selectedItemId}`
        });
        break;
      default:
        const data = {
          status: PERMISSION_REQUEST_STATUS.ACCEPTED
        };

        this.setState({
          isLoading: true
        });

        updateRequest(user, selectedItemId, data).then(res => {
          this.setState({
            isAcceptModalOpen: false,
            selectedItems: []
          });
          this.setItems().then(() => {
            this.props.setNeedUpdate();
          });
        });
    }
  };

  onAcceptAndMergeRequestClick = mergeUser => {
    this.setState({
      isAcceptAndMergeModalOpen: false,
      isAcceptAndMergeRequestInfoModal: true,
      mergeUser: mergeUser
    });
  };

  onAcceptAndMergeRequestInfoModalClick = () => {
    const user = this.props.user;
    const requests = this.state.items;
    const { selectedItems, mergeUser } = this.state;
    const mergeUserId = mergeUser.user.id;
    const [selectedItem] = selectedItems;
    const selectedItemId = selectedItem.id;

    const request = requests.find(requestState => requestState.id === selectedItemId);

    const data = {
      permissionId: request.id,
      userId: request.requesterId
    };

    this.setState({
      isLoading: true
    });

    const promise =
      selectedItem.requestedPermission.preset === ROLE.STUDENT
        ? mergeStudent(user, mergeUserId, data)
        : mergeUserStaff(user, mergeUserId, data);

    promise
      .then(res => {
        this.setState({
          isAcceptAndMergeRequestInfoModal: false,
          selectedItems: []
        });
        this.setItems().then(() => {
          this.props.setNeedUpdate();
        });
      })
      .catch(error => {
        console.error(error);
        this.setState({
          isAcceptAndMergeRequestInfoModal: false,
          isError: true
        });
        this.setItems();
      });
  };

  onCancelButtonAcceptAndMergeRequestInfoModalClick = (): void => {
    this.setState({
      isAcceptAndMergeRequestInfoModal: false
    });
  };

  onCloseErrorClick = (event: Event) => {
    event.preventDefault();
    this.setState({
      isError: false
    });
  };

  renderErrorAlert(): React.ReactNode {
    const { isError } = this.state;

    if (!isError) {
      return null;
    }

    const errorMessage =
      'Unable to merge this user. Some error. Contact Squad In Touch support team if you should have further questions.';
    return (
      <SimpleAlert
        message={errorMessage}
        additionalClassNames={'alert-danger'}
        onCloseAlertClick={this.onCloseErrorClick}
      />
    );
  }

  renderAcceptRequestModal(): React.ReactNode {
    const { selectedItems, items } = this.state;
    const isOneItemSelected = selectedItems.length === 1;
    const request = isOneItemSelected ? items.find(requestState => requestState.id === selectedItems[0].id) : undefined;

    return (
      <AcceptRequestModal
        request={request}
        onCloseClick={this.closeAcceptRequestModal}
        onAcceptClick={this.onAcceptRequestClick}
        isOpen={this.state.isAcceptModalOpen}
      />
    );
  }

  renderAcceptAndMergeRequestForm(): React.ReactNode {
    const { selectedItems, items, isAcceptAndMergeModalOpen } = this.state;
    const { user } = this.props;
    const isOneItemSelected = selectedItems.length === 1;
    const request = isOneItemSelected ? items.find(requestState => requestState.id === selectedItems[0].id) : undefined;

    return (
      <SimpleModal isOpen={isAcceptAndMergeModalOpen}>
        <AcceptAndMergeRequestForm
          request={request}
          onCloseClick={this.onAcceptAndMergeRequestCloseClick}
          onAcceptClick={this.onAcceptAndMergeRequestClick}
          user={user}
        />
      </SimpleModal>
    );
  }

  renderAcceptAndMergeRequestInfoModal(): React.ReactNode {
    const { selectedItems, items, mergeUser, isAcceptAndMergeRequestInfoModal } = this.state;
    const isOneItemSelected = selectedItems.length === 1;
    const request = isOneItemSelected ? items.find(requestState => requestState.id === selectedItems[0].id) : undefined;

    return (
      <AcceptAndMergeRequestInfoModal
        isOpen={isAcceptAndMergeRequestInfoModal}
        request={request}
        mergeUser={mergeUser}
        onAcceptClick={this.onAcceptAndMergeRequestInfoModalClick}
        onCloseClick={this.onCancelButtonAcceptAndMergeRequestInfoModalClick}
      />
    );
  }

  renderRejectConfirmModal(): React.ReactNode {
    const { isRejectConfirmModalOpen, selectedItems, items } = this.state;
    const isOneItemSelected = selectedItems.length === 1;
    const request = isOneItemSelected ? items.find(requestState => requestState.id === selectedItems[0].id) : undefined;
    const requesterName = request ? this.getRequesterName(request) : 'this requester';
    const role = propz.get(request, ['requestedPermission', 'preset'], '');

    return (
      <SimpleModal
        isOpen={isRejectConfirmModalOpen}
        showHeader={false}
        showFooter={false}
        onBackdropClick={this.closeRejectConfirmModal}
        customClass="requestRejectModal"
        dialogStyle={{ maxWidth: 480 }}
      >
        <div className="requestActionModal requestActionModal--embedded">
          <div className="requestActionModal__head">
            <div className="requestActionModal__icon requestActionModal__icon--danger">!</div>
            <div>
              <h5>Reject request?</h5>
              <p>This request will be moved to the archive as rejected.</p>
            </div>
            <button
              type="button"
              className="requestActionModal__close"
              onClick={this.closeRejectConfirmModal}
              aria-label="Close"
            >
              <span>&times;</span>
            </button>
          </div>
          <div className="requestActionModal__body">
            <div className="requestActionModal__notice requestActionModal__notice--danger">
              Are you sure you want to reject the {ROLE_SERVER_TO_CLIENT_MAPPING[role] || role} request from{' '}
              {requesterName}?
            </div>
          </div>
          <div className="requestActionModal__foot">
            <button type="button" className="people-btn people-btn--secondary" onClick={this.closeRejectConfirmModal}>
              Cancel
            </button>
            <button type="button" className="people-btn people-btn--danger" onClick={this.onRejectRequestClick}>
              Reject request
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }
  onAcceptAndMergeRequestCloseClick = (): void => {
    this.setState({
      isAcceptAndMergeModalOpen: false
    });
  };

  openAcceptRequestModal = (): void => {
    const { user } = this.props;
    const isAcceptRolesAvailable = propz.get(user, ['activeSchool', 'canAcceptStaffRoles']);
    const { selectedItems } = this.state;
    const isOneItemSelected = selectedItems.length === 1;
    const request = isOneItemSelected ? selectedItems[0] : undefined;
    const role = propz.get(request, ['requestedPermission', 'preset']);

    if (role === ROLE.SLT && user.activeRole !== ROLE.ADMIN) {
      this.openAcceptRequestDisabledModal();
      return;
    }
    if (role === ROLE.ADMIN || isAcceptRolesAvailable) {
      this.setState({
        isAcceptModalOpen: true
      });
    } else {
      this.openAcceptRequestDisabledModal();
    }
  };

  openAcceptAndMergeRequestModal = (): void => {
    const { user } = this.props;
    const isAcceptRolesAvailable = propz.get(user, ['activeSchool', 'canAcceptStaffRoles']);

    if (isAcceptRolesAvailable) {
      this.setState({
        isAcceptAndMergeModalOpen: true
      });
    } else {
      this.openAcceptRequestDisabledModal();
    }
  };

  renderAcceptRequestDisabledModal = () => {
    const { isAcceptRequestDisabledModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isAcceptRequestDisabledModalOpen}
        title={'Access denied'}
        buttonText={'Ok'}
        onButtonClick={this.closeAcceptRequestDisabledModal}
        body={'This feature is not available in your current subscription plan'}
      />
    );
  };

  openAcceptRequestDisabledModal = () => {
    this.setState({
      isAcceptRequestDisabledModalOpen: true
    });
  };

  closeAcceptRequestDisabledModal = () => {
    this.setState({
      isAcceptRequestDisabledModalOpen: false
    });
  };

  isActiveActionItemForRequest(request: Request): boolean {
    const user = this.props.user;
    const isSubscriptionPlanFree = isUserActiveSchoolSubscriptionPlanFree(user);
    const isSubscriptionPlanFull = isUserActiveSchoolSubscriptionPlanFull(user);
    const isRequestedPermissionPresetParent = propz.get(request, ['requestedPermission', 'preset']) === ROLE.PARENT;

    switch (true) {
      case isSubscriptionPlanFull:
      case isSubscriptionPlanFree && isRequestedPermissionPresetParent:
        return true;
      default:
        return false;
    }
  }
  isActiveActionItem(): boolean {
    const { selectedItems, items } = this.state;
    const user = this.props.user;
    const isOneItemSelected = selectedItems.length === 1;

    if (!isOneItemSelected) {
      return false;
    }

    const isSubscriptionPlanFree = isUserActiveSchoolSubscriptionPlanFree(user);
    const isSubscriptionPlanFull = isUserActiveSchoolSubscriptionPlanFull(user);

    const request = items.find(requestState => requestState.id === selectedItems[0].id);
    const isRequestedPermissionPresetParent = request.requestedPermission.preset === ROLE.PARENT;

    switch (true) {
      case isSubscriptionPlanFull:
      case isSubscriptionPlanFree && isRequestedPermissionPresetParent:
        return true;
      default:
        return false;
    }
  }

  openRejectConfirmModal = (): void => {
    this.setState({ isRejectConfirmModalOpen: true });
  };

  closeRejectConfirmModal = (): void => {
    this.setState({ isRejectConfirmModalOpen: false });
  };

  onRejectRequestClick = () => {
    const user = this.props.user;
    const { selectedItems } = this.state;
    const selectedItem = selectedItems[0];
    const selectedItemId = selectedItem.id;

    const data = {
      status: PERMISSION_REQUEST_STATUS.REJECTED
    };

    this.setState({
      selectedItems: [],
      isRejectConfirmModalOpen: false,
      isLoading: true
    });

    updateRequest(user, selectedItemId, data).then(res => {
      this.setItems().then(() => {
        this.props.setNeedUpdate();
      });
    });
  };

  getRequesterName(request: Request): string {
    const firstName = propz.get(request, ['requester', 'firstName'], '');
    const lastName = propz.get(request, ['requester', 'lastName'], '');
    return `${firstName} ${lastName}`.trim() || 'Unknown requester';
  }

  getRequesterInitials(request: Request): string {
    const firstName = propz.get(request, ['requester', 'firstName'], '?');
    const lastName = propz.get(request, ['requester', 'lastName'], '');
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  }

  getRequestDate(request: Request): string {
    const createdAt = propz.get(request, ['createdAt'], '');
    if (!createdAt) {
      return '-';
    }

    return new Date(createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  getRoleColor(role: string): string {
    switch (role) {
      case ROLE.ADMIN:
        return '#227ebd';
      case ROLE.MANAGER:
        return '#7b5bd6';
      case ROLE.TEACHER:
        return '#2f9e5a';
      case ROLE.COACH:
        return '#f08c1a';
      case ROLE.PARENT:
        return '#0f8a8a';
      case ROLE.STUDENT:
        return '#d64550';
      default:
        return '#64748b';
    }
  }

  renderRoleChip(role: string): React.ReactNode {
    const color = this.getRoleColor(role);
    return (
      <span className="requests-roleChip" style={{ '--requests-role-color': color } as React.CSSProperties}>
        <i />
        <span>{ROLE_SERVER_TO_CLIENT_MAPPING[role] || role}</span>
      </span>
    );
  }

  renderSports(request: Request): React.ReactNode {
    const sports = propz.get(request, ['sports'], []);

    if (sports.length === 0) {
      return <span className="requests-muted">-</span>;
    }

    return (
      <span className="requests-chipList">
        {sports.slice(0, 2).map(sport => (
          <span key={sport.id || sport.name} className="requests-sportChip">
            {sport.name}
          </span>
        ))}
        {sports.length > 2 ? <span className="requests-moreChip">+{sports.length - 2} more</span> : null}
      </span>
    );
  }

  setSimpleFilter = (filterField: string, value: string): void => {
    const isMulti = filterField === 'requestedPermissionPreset' || filterField === 'status';
    this.setState({
      filters: {
        ...this.state.filters,
        [filterField]: isMulti ? (value ? [value] : []) : value
      }
    });
  };

  toggleMultiFilter = (filterField: string, value: string): void => {
    const current = Array.isArray(this.state.filters[filterField]) ? this.state.filters[filterField] : [];
    this.setState({
      filters: {
        ...this.state.filters,
        [filterField]: current.includes(value) ? current.filter(item => item !== value) : [...current, value]
      }
    });
  };

  setSearchFilter = (filterField: string, value: string): void => {
    this.setState({ filters: { ...this.state.filters, [filterField]: value } });
  };

  setDateRangeFilter = (value: any): void => {
    const requestDateRange = value || { start: null, end: null };
    this.setState(
      {
        requestDateRange,
        filters: {
          ...this.state.filters,
          createdAt: {
            from: requestDateRange.start ? moment(requestDateRange.start).format('DD-MM-YYYY') : '',
            to: requestDateRange.end ? moment(requestDateRange.end).format('DD-MM-YYYY') : ''
          }
        }
      },
      this.onApplyFilterClick
    );
  };

  setDateFilter = (edge: string, value: string): void => {
    const currentFilterField = this.state.filters.createdAt || {};
    this.setState({
      filters: {
        ...this.state.filters,
        createdAt: {
          ...currentFilterField,
          [edge]: value
        }
      }
    });
  };

  getFilterValue(filterField: string): string {
    const value = this.state.filters[filterField];
    return Array.isArray(value) ? propz.get(value, [0], '') : value || '';
  }

  isRequestSelected(request: Request): boolean {
    return this.state.selectedItems.some(item => item.id === request.id);
  }

  onRequestCheckboxChange = (request: Request): void => {
    const { selectedItems } = this.state;
    const isSelected = this.isRequestSelected(request);
    this.setState({
      selectedItems: isSelected ? selectedItems.filter(item => item.id !== request.id) : [...selectedItems, request],
      isSelectAllChecked: false
    });
  };

  onCurrentPageToggle = (): void => {
    const { items, selectedItems } = this.state;
    const isAllCurrentPageSelected =
      items.length > 0 && items.every(item => selectedItems.some(selectedItem => selectedItem.id === item.id));

    if (isAllCurrentPageSelected) {
      this.setState({
        selectedItems: selectedItems.filter(selectedItem => items.every(item => item.id !== selectedItem.id)),
        isSelectAllChecked: false
      });
    } else {
      this.onSelectAllOnPageClick();
    }
  };

  renderPagination(): React.ReactNode {
    const { currentPage, total } = this.state;
    return total > 0 ? (
      <Pagination2 modern currentPage={currentPage} total={total} setCurrentPageParams={this.setCurrentPageParams} />
    ) : null;
  }

  renderFilters(filterOptions): React.ReactNode {
    return (
      <form
        className="people-filter requests-filter"
        onSubmit={event => {
          event.preventDefault();
          this.onApplyFilterClick();
        }}
      >
        <div className="people-filter__header">
          <strong>Filters</strong>
          <button
            type="button"
            onClick={() => {
              this.setState({ isShowFilter: false });
              this.setItems();
            }}
            aria-label="Close filters"
          >
            ×
          </button>
        </div>
        <div className="people-filter__body">
          <label className="people-filter__field">
            <span>Email</span>
            <input
              value={this.getFilterValue('requester.email')}
              placeholder="Search by email"
              onChange={event => this.setSimpleFilter('requester.email', event.target.value)}
            />
          </label>
          <label className="people-filter__field">
            <span>Phone</span>
            <input
              value={this.getFilterValue('requester.phone')}
              placeholder="Search by phone"
              onChange={event => this.setSimpleFilter('requester.phone', event.target.value)}
            />
          </label>
          <div className="requests-dateRange">
            <SitDatePicker
              label="Request date"
              mode="range"
              value={this.state.requestDateRange}
              onChange={this.setDateRangeFilter}
              format="DD-MM-YYYY"
              placeholder="Select date range"
              numberOfMonths={2}
              align="left"
              clearable={true}
            />
          </div>
        </div>
        <div className="people-filter__actions">
          <button type="button" className="people-btn people-btn--ghost" onClick={this.onClearFilterClick}>
            Clear all
          </button>
          <div>
            <button
              type="button"
              className="people-btn people-btn--secondary"
              onClick={() => {
                this.setState({ isShowFilter: false });
                this.setItems();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="people-btn people-btn--primary">
              Apply
            </button>
          </div>
        </div>
      </form>
    );
  }

  openRequestPreview = (request: Request): void => {
    this.setState({ previewItem: request });
  };

  closeRequestPreview = (): void => {
    this.setState({ previewItem: undefined });
  };

  runActionForRequest = (request: Request, action: () => void): void => {
    this.setState({ selectedItems: [request], isSelectAllChecked: false }, action);
  };

  renderDetailField(label: string, value: React.ReactNode): React.ReactNode {
    return (
      <div className="requests-drawerField">
        <span>{label}</span>
        <strong>{value || '-'}</strong>
      </div>
    );
  }

  renderRequestDrawer(): React.ReactNode {
    const { previewItem } = this.state;

    if (!previewItem) {
      return null;
    }

    const role = propz.get(previewItem, ['requestedPermission', 'preset'], '');
    const comment = propz.get(previewItem, ['requestedPermission', 'comment'], '');
    const email = propz.get(previewItem, ['requester', 'email'], '');
    const phone = propz.get(previewItem, ['requester', 'phone'], '');
    const isStudent = role === ROLE.STUDENT;
    const canAction = this.isActiveActionItemForRequest(previewItem);

    return (
      <>
        <div className="requests-drawerScrim" onClick={this.closeRequestPreview} />
        <aside className="requests-drawer" role="dialog" aria-modal="true">
          <div className="requests-drawer__head">
            <div className="requests-drawer__identity">
              <span className="requests-avatar requests-avatar--lg">
                <span>{this.getRequesterInitials(previewItem)}</span>
              </span>
              <div>
                <h2>{this.getRequesterName(previewItem)}</h2>
                <p>
                  {ROLE_SERVER_TO_CLIENT_MAPPING[role] || role} request · {this.getRequestDate(previewItem)}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="requests-drawer__close"
              onClick={this.closeRequestPreview}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="requests-drawer__body">
            <section className="requests-drawerSection">
              <div className="requests-drawerSection__title">Requested role</div>
              {this.renderRoleChip(role)}
            </section>
            <section className="requests-drawerSection">
              <div className="requests-drawerSection__title">Contact</div>
              {this.renderDetailField('Email', email)}
              {this.renderDetailField('Phone', phone)}
            </section>
            {!isUserAdminSU(this.props.user) ? (
              <section className="requests-drawerSection">
                <div className="requests-drawerSection__title">Sports</div>
                {this.renderSports(previewItem)}
              </section>
            ) : null}
            <section className="requests-drawerSection">
              <div className="requests-drawerSection__title">Message</div>
              <div className="requests-drawerMessage">{comment || 'No message left with this request.'}</div>
            </section>
          </div>
          <div className="requests-drawer__foot">
            <button
              type="button"
              className="people-btn people-btn--danger"
              disabled={!canAction}
              onClick={() => this.runActionForRequest(previewItem, this.openRejectConfirmModal)}
            >
              Reject
            </button>
            {isStudent ? (
              <button
                type="button"
                className="people-btn people-btn--secondary"
                disabled={!canAction}
                onClick={() => this.runActionForRequest(previewItem, this.openAcceptAndMergeRequestModal)}
              >
                Accept & merge
              </button>
            ) : null}
            <button
              type="button"
              className="people-btn people-btn--primary"
              disabled={!canAction || isStudent}
              onClick={() => this.runActionForRequest(previewItem, this.openAcceptRequestModal)}
            >
              Accept
            </button>
          </div>
        </aside>
      </>
    );
  }
  renderRequestTable(): React.ReactNode {
    const { items, selectedItems, currentPage, total } = this.state;
    const isUnionAdmin = isUserAdminSU(this.props.user);
    const isAllCurrentPageSelected =
      items.length > 0 && items.every(item => selectedItems.some(selectedItem => selectedItem.id === item.id));

    if (items.length === 0) {
      return (
        <div className="requests-empty">
          <div className="requests-empty__icon">!</div>
          <h3>No new requests</h3>
          <p>
            {isUserAdminSU(this.props.user)
              ? 'Role requests will appear here for review.'
              : 'Role requests from staff, parents, coaches and students will appear here for review.'}
          </p>
        </div>
      );
    }

    return (
      <div className="people-tableCard requests-tableCard">
        <div className="people-tableScroll">
          <table
            className={`people-table requests-table ${this.state.hiddenColumns
              .map(key => `requests-hide-${key}`)
              .join(' ')}`}
          >
            <thead>
              <tr>
                <th className="people-table__check">
                  <SelectAllCheckbox
                    compact
                    isChecked={isAllCurrentPageSelected}
                    totalCount={total}
                    currentPage={currentPage}
                    pageItemsCount={items.length}
                    selectedItems={selectedItems}
                    onSelectAllOnPageClick={this.onSelectAllOnPageClick}
                    onSelectAllClick={this.onSelectAllClick}
                    onUnselectAllClick={this.onUnselectAllClick}
                  />
                </th>
                <th className="people-table__sortable" onClick={() => this.onTableSortClick('createdAt')}>
                  Date
                </th>
                <th className="people-table__sortable" onClick={() => this.onTableSortClick('requester.firstName')}>
                  Requester
                </th>
                <th>Role</th>
                <th>Phone</th>
                {!isUnionAdmin ? <th>Sports</th> : null}
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {items.map(request => {
                const isSelected = this.isRequestSelected(request);
                const email = propz.get(request, ['requester', 'email'], '');
                const phone = propz.get(request, ['requester', 'phone'], '');
                const role = propz.get(request, ['requestedPermission', 'preset'], '');
                const comment = propz.get(request, ['requestedPermission', 'comment'], '');

                return (
                  <tr
                    key={request.id}
                    data-selected={isSelected}
                    data-preview={propz.get(this.state.previewItem, ['id']) === request.id}
                    onClick={() => this.openRequestPreview(request)}
                  >
                    <td className="people-table__check" onClick={event => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => this.onRequestCheckboxChange(request)}
                      />
                    </td>
                    <td className="requests-dateCell">{this.getRequestDate(request)}</td>
                    <td>
                      <div className="requests-personCell">
                        <span className="requests-avatar">
                          <span>{this.getRequesterInitials(request)}</span>
                        </span>
                        <div>
                          <strong>{this.getRequesterName(request)}</strong>
                          <span>{email || '-'}</span>
                        </div>
                      </div>
                    </td>
                    <td>{this.renderRoleChip(role)}</td>
                    <td>
                      <span className="requests-contactCell">{phone || '-'}</span>
                    </td>
                    {!isUnionAdmin ? <td>{this.renderSports(request)}</td> : null}
                    <td>
                      <span className="requests-commentCell" title={comment}>
                        {comment || '-'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const {
      selectedItems,
      filters,
      isLoading,
      total,
      isAcceptModalOpen,
      isAcceptAndMergeModalOpen,
      isAcceptAndMergeRequestInfoModal,
      isRejectConfirmModalOpen,
      isError
    } = this.state;

    const request = selectedItems[0];
    const role = propz.get(request, ['requestedPermission', 'preset']);
    const isStudent = role === ROLE.STUDENT;
    const selectedCount = selectedItems.length;

    if (isLoading) {
      return <Loader />;
    }

    const classes =
      isAcceptModalOpen ||
      isAcceptAndMergeModalOpen ||
      isAcceptAndMergeRequestInfoModal ||
      isRejectConfirmModalOpen ||
      isError
        ? 'requests-modernScreen modal-open'
        : 'requests-modernScreen';

    const filterOptions = {};

    return (
      <div className={classes}>
        {this.renderErrorAlert()}
        {this.renderAcceptAndMergeRequestInfoModal()}
        {this.renderRejectConfirmModal()}
        {this.renderAcceptRequestModal()}
        {this.renderAcceptAndMergeRequestForm()}
        {this.renderAcceptRequestDisabledModal()}
        <div className="people-toolbar requests-toolbar">
          <div className="requests-toolbar__row requests-toolbar__row--primary">
            <label className="unionAdminsSearch requests-nameSearch">
              <input
                value={this.getFilterValue('nameSearch')}
                placeholder="Search by name or surname…"
                onChange={event => this.setSimpleFilter('nameSearch', event.target.value)}
                onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
              />
            </label>
            <div className="requests-toolbar__controls">
              <button
                type="button"
                className={`people-btn people-btn--secondary people-btn--iconLeft people-filterBtn ${
                  this.state.isShowFilter ? 'people-btn--on' : ''
                }`}
                onClick={this.onTableFilterClick}
              >
                <span className="people-icon people-icon--filter" />
                Filters
                {countActivePeopleFilters(filters) > 0 && (
                  <span className="people-btn__badge">{countActivePeopleFilters(filters)}</span>
                )}
              </button>
              <PeopleColumnsMenu
                columns={[
                  { key: 'date', label: 'Date' },
                  { key: 'requester', label: 'Requester' },
                  { key: 'role', label: 'Role' },
                  { key: 'phone', label: 'Phone' },
                  ...(!isUserAdminSU(this.props.user) ? [{ key: 'sports', label: 'Sports' }] : []),
                  { key: 'comment', label: 'Comment' }
                ]}
                hidden={this.state.hiddenColumns}
                onChange={hiddenColumns => {
                  saveHiddenColumns(this.props.user, 'people.roleRequests.inbox', hiddenColumns);
                  this.setState({ hiddenColumns });
                }}
              />
            </div>
          </div>
        </div>
        <div
          className={`requests-contentLayout ${this.state.isShowFilter ? 'requests-contentLayout--withFilter' : ''}`}
        >
          {this.state.isShowFilter ? (
            <>
              <div
                className="requests-filterScrim"
                onClick={() => {
                  this.setState({ isShowFilter: false });
                  this.setItems();
                }}
              />
              {this.renderFilters(filterOptions)}
            </>
          ) : null}
          <div className="requests-contentMain">
            <PeopleFilterChips
              filters={filters}
              columns={COLUMNS}
              valueLabels={{}}
              onRemove={field => {
                const nextState: any = { filters: { ...filters, [field]: '' } };
                if (field === 'createdAt') nextState.requestDateRange = { start: null, end: null };
                this.setState(nextState, this.onApplyFilterClick);
              }}
              onClear={this.onClearFilterClick}
            />
            {selectedCount > 0 ? (
              <div className="people-selection requests-selection">
                <span className="people-selection__summary">{selectedCount} selected</span>
                <div className="people-selection__actions">
                  <button
                    type="button"
                    className="people-btn people-btn--primary"
                    disabled={!this.isActiveActionItem() || isStudent}
                    onClick={this.openAcceptRequestModal}
                  >
                    Accept request
                  </button>
                  <button
                    type="button"
                    className="people-btn people-btn--secondary"
                    disabled={!this.isActiveActionItem() || !isStudent}
                    onClick={this.openAcceptAndMergeRequestModal}
                  >
                    Accept & merge
                  </button>
                  <button
                    type="button"
                    className="people-btn people-btn--danger"
                    disabled={!this.isActiveActionItem()}
                    onClick={this.openRejectConfirmModal}
                  >
                    Reject request
                  </button>
                  <button type="button" className="people-btn people-btn--secondary" onClick={this.onSelectAllClick}>
                    Select all filtered
                  </button>
                </div>
                <div className="people-toolbar__spacer" />
                <button type="button" className="people-selection__clear" onClick={this.onUnselectAllClick}>
                  Clear selection
                </button>
              </div>
            ) : null}
            {this.renderRequestTable()}
            {this.renderPagination()}
          </div>
        </div>
        {this.renderRequestDrawer()}
      </div>
    );
  }
}
